import React from 'react';
import { Layers } from 'lucide-react';
import { fmtKwh, fmtNum, fmtPct } from '../../../../src/lib/funnelUtils';
import { CHANNELS, getChannel } from '../../../../src/lib/canalComercial';
import { CustomRange, Periodo, inPeriodo } from '../../../../src/lib/periodoFiltro';
import { aprovacaoAindaValida } from '../../../../src/lib/fechamentoMensal';
import { jornadaNoPeriodo } from '../../../../src/lib/dashboard/funilJornadaNoPeriodo';
import type { ContagemFunil } from '../../../../src/lib/dashboard/funilJornadaNoPeriodo';
import { Profile } from '../../../../types';
import { NegRow } from '../types';

interface ChannelPerformanceBlockProps {
    /** Sem o filtro de período (ex.: filteredExcetoPeriodo) — este componente resolve
     * coorte (leads) e evento (aprovados/assinados) cada um com sua própria data. */
    rows: NegRow[];
    periodo: Periodo;
    customRange: CustomRange;
    profiles: Profile[];
    /** canal → meta_kwh: soma das metas da Folha de quem é do canal (ver src/lib/metasComerciais.ts). */
    metasMap: Record<string, number>;
    /** Só gerente/adm com um mês-calendário único selecionado vê meta/gap/contribuição. */
    showMeta: boolean;
    /** O seletor "Contar" da tela: 'jornada' só conta quem teve as etapas até a
     *  aprovação/assinatura dentro do período (ver funilJornadaNoPeriodo.ts). */
    contagem?: ContagemFunil;
}

/** Performance por Canal — agregação client-side por canal comercial (Comercial ×
 * Parceiros, ver canalComercial.ts). "Leads" é coorte (criados no período); "Aprovados"/kWh aprovado/
 * assinado são por data do evento (aprovação/assinatura), não de criação do lead —
 * mesmo critério do bloco de Meta x Realizado, pra não misturar coorte com evento. */
const ChannelPerformanceBlock: React.FC<ChannelPerformanceBlockProps> = ({ rows, periodo, customRange, profiles, metasMap, showMeta: podeVerMeta, contagem = 'evento' }) => {
    const canalOf = (id?: string | null) => getChannel(profiles.find(p => p.id === id));
    const naoPerdidos = rows.filter(r => r.status !== 'perdido');
    const coorte = naoPerdidos.filter(r => inPeriodo(r.created_at, periodo, customRange));
    const soNoPeriodo = contagem === 'jornada';
    // A meta é de kWh aprovado no mês, venha de quando vier: contra o recorte "só o
    // que aconteceu no período" o gap sairia maior do que é. Por isso some nesse modo.
    const showMeta = podeVerMeta && !soNoPeriodo;
    const jornadaOk = (r: NegRow, etapa: string) => !soNoPeriodo || jornadaNoPeriodo(r as any, etapa, periodo, customRange);

    const data = CHANNELS.map(ch => {
        const total = coorte.filter(r => canalOf(r.responsavel_id) === ch).length;

        const chTodos = naoPerdidos.filter(r => canalOf(r.responsavel_id) === ch);
        const aprovadosRows = chTodos.filter(r => {
            const dataAprovacao = aprovacaoAindaValida(r);
            return !!dataAprovacao && inPeriodo(dataAprovacao, periodo, customRange) && jornadaOk(r, 'termo_aprovado');
        });
        const kwhAprovado = aprovadosRows.reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
        const kwhAssinado = chTodos
            .filter(r => r.data_assinatura_termo && inPeriodo(r.data_assinatura_termo, periodo, customRange) && jornadaOk(r, 'termo_assinado'))
            .reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
        const conversao = total > 0 ? (aprovadosRows.length / total) * 100 : 0;
        const meta = showMeta ? (metasMap[ch] || 0) : 0;
        const gap = Math.max(0, meta - kwhAprovado);
        return { canal: ch, total, aprovados: aprovadosRows.length, kwhAprovado, kwhAssinado, conversao, meta, gap };
    });
    const totalKwhAprovado = data.reduce((s, d) => s + d.kwhAprovado, 0);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <Layers size={15} className="text-primary" /> Performance por Canal
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Comercial × Parceiros · Leads = criados no período · {soNoPeriodo
                    ? 'Aprovado/Assinado = só quem teve todas as etapas dentro do período (meta e gap aparecem na contagem "Pela data da etapa")'
                    : 'Aprovado/Assinado = por data do evento'}
            </p>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-700">
                            <th className="py-2 pr-3 font-semibold">Canal</th>
                            <th className="py-2 pr-3 font-semibold text-right">Leads</th>
                            <th className="py-2 pr-3 font-semibold text-right">Aprovados</th>
                            <th className="py-2 pr-3 font-semibold text-right">kWh aprovado</th>
                            <th className="py-2 pr-3 font-semibold text-right">kWh assinado</th>
                            <th className="py-2 pr-3 font-semibold text-right">Conversão</th>
                            <th className="py-2 pr-3 font-semibold text-right">% do total</th>
                            {showMeta && <th className="py-2 pr-3 font-semibold text-right">Meta</th>}
                            {showMeta && <th className="py-2 font-semibold text-right">Gap</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map(d => (
                            <tr key={d.canal} className="border-b border-gray-50 dark:border-gray-700/50">
                                <td className="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100">{d.canal}</td>
                                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{fmtNum(d.total)}</td>
                                <td className="py-2 pr-3 text-right text-green-600 dark:text-green-400 font-semibold">{fmtNum(d.aprovados)}</td>
                                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{fmtKwh(d.kwhAprovado)}</td>
                                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{fmtKwh(d.kwhAssinado)}</td>
                                <td className="py-2 pr-3 text-right font-bold text-blue-600 dark:text-blue-400">{fmtPct(d.conversao)}</td>
                                <td className="py-2 pr-3 text-right text-gray-500 dark:text-gray-400">{totalKwhAprovado > 0 ? fmtPct((d.kwhAprovado / totalKwhAprovado) * 100) : '—'}</td>
                                {showMeta && <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{fmtKwh(d.meta)}</td>}
                                {showMeta && <td className={`py-2 text-right font-semibold ${d.gap > 0 ? 'text-red-500' : 'text-green-600 dark:text-green-400'}`}>{fmtKwh(d.gap)}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ChannelPerformanceBlock;
